import React, { useMemo } from "react";
import styled, { keyframes } from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { getLessonsLeft, getTimeLeftLabel } from "./streakFreezeUtils";

const snowfall = keyframes`
  0% { transform: translateY(-20px) rotate(0deg); opacity: 0; }
  10% { opacity: 1; }
  100% { transform: translateY(520px) rotate(360deg); opacity: 0; }
`;

const shimmer = keyframes`
  0% { transform: translateX(-120%) skewX(-20deg); }
  60%, 100% { transform: translateX(220%) skewX(-20deg); }
`;

const ModalOverlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(8, 24, 48, 0.82);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10001;
  padding: 20px;
`;

const ModalContent = styled(motion.div)`
  position: relative;
  overflow: hidden;
  background: linear-gradient(165deg, #f2fbff 0%, #d4efff 55%, #b6e2ff 100%);
  border-radius: 24px;
  padding: clamp(16px, 3vh, 28px) clamp(16px, 3vw, 28px);
  text-align: center;
  max-width: 400px;
  width: 100%;
  box-shadow: 0 20px 50px rgba(0, 60, 120, 0.45),
    inset 0 0 0 2px rgba(255, 255, 255, 0.7);
  display: flex;
  flex-direction: column;
  align-items: center;
  font-family: "Quicksand", sans-serif;
`;

const Snowflake = styled.span`
  position: absolute;
  top: -20px;
  left: ${(props) => props.left}%;
  font-size: ${(props) => props.size}px;
  color: #ffffff;
  text-shadow: 0 0 6px rgba(120, 190, 255, 0.9);
  animation: ${snowfall} ${(props) => props.duration}s linear infinite;
  animation-delay: ${(props) => props.delay}s;
  pointer-events: none;
`;

const Badge = styled.div`
  position: relative;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 2px;
  color: #ffffff;
  background: linear-gradient(90deg, #3a9bdc, #5bc0ff);
  padding: 6px 14px;
  border-radius: 999px;
  box-shadow: 0 3px 0 #2878b0;
`;

const IceBlock = styled(motion.div)`
  position: relative;
  overflow: hidden;
  margin: clamp(12px, 2.5vh, 22px) 0 clamp(8px, 1.5vh, 14px);
  width: clamp(130px, 22vh, 170px);
  height: clamp(130px, 22vh, 170px);
  border-radius: 28px;
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.95), rgba(160, 215, 255, 0.85));
  border: 3px solid rgba(255, 255, 255, 0.9);
  box-shadow: 0 10px 30px rgba(40, 120, 200, 0.35),
    inset 0 -10px 20px rgba(90, 170, 240, 0.35);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 40%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.8), transparent);
    animation: ${shimmer} 3.2s ease-in-out infinite;
  }
`;

const IceCrack = styled.svg`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0.55;
  pointer-events: none;
`;

const FrozenNumber = styled.div`
  position: relative;
  font-size: clamp(52px, 10vh, 76px);
  font-weight: 800;
  line-height: 1;
  color: #2b8fd6;
  text-shadow: 0 2px 0 #ffffff, 0 6px 14px rgba(43, 143, 214, 0.45);
`;

const FrozenLabel = styled.div`
  position: relative;
  font-size: 13px;
  font-weight: 700;
  color: #4a90c2;
  letter-spacing: 1px;
  margin-top: 4px;
`;

const CornerFlake = styled.div`
  position: absolute;
  top: 6px;
  right: 10px;
  font-size: 22px;
`;

const Title = styled.div`
  position: relative;
  font-size: clamp(19px, 3vh, 24px);
  font-weight: 800;
  color: #12456e;
  margin-bottom: 6px;
`;

const Subtitle = styled.div`
  position: relative;
  font-size: clamp(13px, 1.9vh, 15px);
  color: #3d6688;
  line-height: 1.45;
  margin-bottom: clamp(10px, 2vh, 16px);
`;

const SlotsRow = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  gap: clamp(6px, 1.5vw, 10px);
  padding: 12px 14px;
  background: rgba(255, 255, 255, 0.75);
  border-radius: 18px;
  border: 2px solid rgba(255, 255, 255, 0.95);
  width: 100%;
  box-sizing: border-box;
`;

const Slot = styled(motion.div)`
  width: clamp(40px, 7vh, 50px);
  height: clamp(40px, 7vh, 50px);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: clamp(20px, 3.4vh, 26px);
  background: ${(props) =>
    props.done
      ? "linear-gradient(145deg, #ffb347, #ff6b35)"
      : "linear-gradient(145deg, #e9f6ff, #c6e6fb)"};
  box-shadow: ${(props) =>
    props.done
      ? "0 3px 0 #d4541f, 0 0 14px rgba(255, 120, 50, 0.55)"
      : "0 3px 0 #9ccbe8"};
  filter: ${(props) => (props.done ? "none" : "saturate(0.8)")};
`;

const ProgressText = styled.div`
  position: relative;
  margin-top: 10px;
  font-size: 13px;
  font-weight: 700;
  color: #2b6d9c;
`;

const CountdownChip = styled.div`
  position: relative;
  margin-top: 12px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 800;
  color: ${(props) => (props.urgent ? "#ffffff" : "#12456e")};
  background: ${(props) =>
    props.urgent ? "linear-gradient(90deg, #ff7a45, #ff4d4f)" : "rgba(255, 255, 255, 0.85)"};
  box-shadow: ${(props) => (props.urgent ? "0 3px 0 #c9302c" : "0 3px 0 #9ccbe8")};
`;

const ThawButton = styled(motion.button)`
  position: relative;
  padding: clamp(0.7rem, 1.6vh, 1rem) 2rem;
  border-radius: 16px;
  font-size: clamp(0.9rem, 1.9vh, 1.05rem);
  font-weight: 800;
  letter-spacing: 1px;
  cursor: pointer;
  font-family: "Quicksand", sans-serif;
  width: 100%;
  background: #8747d4;
  color: white;
  border: none;
  box-shadow: 0 4px 0 #6535a3;
  top: 0;
  margin-top: clamp(14px, 2.5vh, 22px);

  &:hover {
    background: #9859e0;
  }

  &:active {
    top: 4px;
    box-shadow: 0 0 0 #6535a3;
  }
`;

const FinePrint = styled.div`
  position: relative;
  margin-top: 10px;
  font-size: 11px;
  color: #5b84a6;
`;

const StreakFrozenModal = ({ isOpen, onClose, recovery }) => {
  const snowflakes = useMemo(
    () =>
      Array.from({ length: 14 }, (_, i) => ({
        id: i,
        left: Math.random() * 100,
        size: 10 + Math.random() * 12,
        duration: 5 + Math.random() * 5,
        delay: Math.random() * 6,
      })),
    []
  );

  if (!recovery) return null;

  const lessonsLeft = getLessonsLeft(recovery);
  const urgent = recovery.daysLeft <= 0;

  return (
    <AnimatePresence>
      {isOpen && (
        <ModalOverlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <ModalContent
            initial={{ scale: 0.3, opacity: 0, rotate: -4 }}
            animate={{
              scale: 1,
              opacity: 1,
              rotate: 0,
              transition: { type: "spring", stiffness: 280, damping: 18 },
            }}
            exit={{ scale: 0.3, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            {snowflakes.map((flake) => (
              <Snowflake key={flake.id} {...flake}>
                ❄
              </Snowflake>
            ))}

            <Badge>STREAK FROZEN</Badge>

            <IceBlock
              animate={{ rotate: [0, -2, 2, -1, 0] }}
              transition={{ duration: 0.6, delay: 0.4, repeat: Infinity, repeatDelay: 3 }}
            >
              <IceCrack viewBox="0 0 100 100" preserveAspectRatio="none">
                <polyline
                  points="8,20 24,34 18,48 34,62"
                  fill="none"
                  stroke="#ffffff"
                  strokeWidth="1.5"
                />
                <polyline
                  points="92,70 78,60 84,46 70,38"
                  fill="none"
                  stroke="#ffffff"
                  strokeWidth="1.5"
                />
              </IceCrack>
              <CornerFlake>❄️</CornerFlake>
              <FrozenNumber>{recovery.frozenStreak}</FrozenNumber>
              <FrozenLabel>DAY STREAK</FrozenLabel>
            </IceBlock>

            <Title>Your {recovery.frozenStreak}-day streak is frozen!</Title>
            <Subtitle>
              Don't worry, it's safe on ice. Finish{" "}
              <strong>{recovery.lessonsRequired} new lessons</strong> to thaw it and
              claim it back.
            </Subtitle>

            <SlotsRow>
              {Array.from({ length: recovery.lessonsRequired }, (_, i) => {
                const done = i < recovery.lessonsCompleted;
                return (
                  <Slot
                    key={i}
                    done={done}
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.25 + i * 0.08, type: "spring", stiffness: 400 }}
                  >
                    {done ? "🔥" : "❄️"}
                  </Slot>
                );
              })}
            </SlotsRow>
            <ProgressText>
              {recovery.lessonsCompleted}/{recovery.lessonsRequired} lessons done
              {lessonsLeft > 0 ? `, ${lessonsLeft} to go` : ""}
            </ProgressText>

            <CountdownChip urgent={urgent}>⏳ {getTimeLeftLabel(recovery)}</CountdownChip>

            <ThawButton
              onClick={onClose}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              THAW MY STREAK 🔥
            </ThawButton>
            <FinePrint>Only lessons you haven't finished before count.</FinePrint>
          </ModalContent>
        </ModalOverlay>
      )}
    </AnimatePresence>
  );
};

export default StreakFrozenModal;
